import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgLink,
  IgSelect,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  PendingPluginPerformanceTestType,
  PluginPerformanceType,
} from "@intelligems/ig-types";
import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";

import { usePluginPerformanceTestStatus } from "../../api/plugin-configs/get-plugin-performance-test-results";
import { useGetPerformanceResults } from "../../api/plugin-performances/get-performance-results";
import { useRequestPluginPerformance } from "../../api/plugin-performances/request-performance";
import { useWebsocket } from "../../hooks/websocket";
import {
  addPerformanceView,
  selectedPerformanceViews,
} from "../../store/slices/appSettings";
import { Debug } from "../../utils/Debug";
import Loading from "../../utils/Loading";

import { PerformanceLineChart } from "./PerformanceLineChart";
import { PerformanceTable } from "./PerformanceTable";
import { formatPerformanceData } from "./utils";

interface PendingTestProps {
  test: PendingPluginPerformanceTestType;
}

const PendingTest = ({ test }: PendingTestProps) => {
  const [enabled, setEnabled] = useImmer<boolean>(true);
  const testStatus = usePluginPerformanceTestStatus(
    test.testId,
    enabled,
    setEnabled
  );

  return (
    <Grid item key={test.testId} sx={{ marginLeft: "10px" }}>
      <IgLink
        onClick={() => {
          window.open(test.url, "_blank");
        }}
      >
        {test.label}
      </IgLink>
      ... <IgTypography>{testStatus.data?.statusText || null}</IgTypography>
    </Grid>
  );
};

export const PerformanceRoute = () => {
  const [recheckPerformanceClicked, setRecheckPerformanceClicked] =
    useImmer<boolean>(false);
  const [counter, setCounter] = useImmer<number>(120);
  const [themeId, setThemeId] = useImmer<string>("");
  const [pendingTests, setPendingTests] = useImmer<
    PendingPluginPerformanceTestType[]
  >([]);
  const [minutes, setMinutes] = useImmer<string>("1");
  const [data, setData] = useImmer<PluginPerformanceType[]>([]);

  const pluginPerformance = useGetPerformanceResults();
  const dispatch = useDispatch();
  const performanceViews = useSelector(selectedPerformanceViews);

  const requestPluginPerformance = useRequestPluginPerformance(setPendingTests);

  useEffect(() => {
    if (pluginPerformance.isSuccess) {
      const newData = pluginPerformance.data.results;
      setData(formatPerformanceData(newData, parseInt(minutes)));
    }
  }, [pluginPerformance.isSuccess, pluginPerformance.data, minutes]);

  useEffect(() => {
    if (recheckPerformanceClicked && counter > 0) {
      setTimeout(() => setCounter(counter - 1), 1000);
    } else {
      setRecheckPerformanceClicked(false);
    }
  }, [counter, recheckPerformanceClicked]);

  const { lastJsonMessage } = useWebsocket();

  useEffect(() => {
    if (lastJsonMessage && lastJsonMessage.type === "pluginPerformance") {
      setData((draft) => {
        draft = [
          ...draft,
          ...formatPerformanceData([lastJsonMessage.item], parseInt(minutes)),
        ];
        return draft;
      });
      toast.success("Performance data updated");
    }
  }, [lastJsonMessage]);

  if (!pluginPerformance.isSuccess) {
    return <Loading />;
  }

  return (
    <Grid container item direction={"column"} rowSpacing={5}>
      <Grid
        container
        item
        justifyContent={"space-between"}
        direction={"row"}
        flexWrap={"nowrap"}
        columnSpacing={3}
      >
        <Grid item>
          <IgButton
            sx={{ minWidth: "110px" }}
            variant={"outlined"}
            onClick={() => dispatch(addPerformanceView())}
          >
            Add Graph
          </IgButton>
        </Grid>
        <Grid item sx={{ minWidth: "100px" }}>
          <IgSelect
            id={`performance-view-select-grouping}`}
            value={minutes}
            onChange={(e: SelectChangeEvent<any>) => {
              setMinutes(e.target.value);
            }}
          >
            {["1", "5", "10", "15", "60"].map((item) => (
              <MenuItem key={item} value={item}>
                {item} Minutes
              </MenuItem>
            ))}
          </IgSelect>
        </Grid>
        <Grid
          container
          item
          columnSpacing={3}
          alignItems={"flex-end"}
          justifyContent={"end"}
        >
          <Grid item>
            <IgTypography>
              {pluginPerformance.data.testBalance} Credits Available
            </IgTypography>
          </Grid>
          <Grid item>
            <IgButton
              disabled={recheckPerformanceClicked}
              onClick={() => {
                setRecheckPerformanceClicked(true);
                requestPluginPerformance.mutate({
                  queryParams: undefined,
                  pathParams: undefined,
                  body: { themeId: themeId || undefined },
                });
              }}
              variant={"contained"}
            >
              {recheckPerformanceClicked
                ? `Disabled for ${counter} seconds`
                : "Recheck Performance (x6 Credits)"}
            </IgButton>
          </Grid>
          <Grid item>
            <IgTextField
              label={"Theme ID (Optional)"}
              value={themeId}
              onChange={(e) => setThemeId(e.target.value)}
            />
          </Grid>
        </Grid>
      </Grid>
      {pendingTests.length ? (
        <Grid container item direction={"column"}>
          <Grid item>
            <IgTypography variant={"body3"}>Pending Tests:</IgTypography>
          </Grid>
          {pendingTests.map((test) => (
            <PendingTest key={test.testId} test={test} />
          ))}
        </Grid>
      ) : null}
      {performanceViews.map((view, index) => (
        <Grid item key={`view-${index}`}>
          <PerformanceLineChart
            data={data}
            index={index}
            view={view}
            total={performanceViews.length}
          />
        </Grid>
      ))}
      <Grid item>
        <PerformanceTable data={data} />
      </Grid>
      <Grid item textAlign={"center"}>
        <IgTypography variant={"body2"}>
          Performance metrics generated by{" "}
          <IgLink
            onClick={() => {
              window.open("https://webpagetest.org", "_blank");
            }}
          >
            WebPageTest.org
          </IgLink>
          .
          <br /> All performance metrics are simulated on a Google Chrome Mobile
          Device
          <br />
          with fast 3G from Virginia USA. Results shown are the median of 3{" "}
          <i>first view</i> runs.
        </IgTypography>
      </Grid>
      <Grid item>
        <Debug data={data} />
      </Grid>
    </Grid>
  );
};
